import type { IntersectionDefinition } from '../types/intersection';
import { ColorCode as CC } from '../types/intersection';
import type { NewRegulatorOverride } from './overridesRuntime';

// Mismo placeholder que usa el generador real (reguladores-scg.ts) — geometry
// no se usa en ningún componente de render, así que un polígono degenerado
// no tiene efecto visual.
const EMPTY_GEOMETRY: IntersectionDefinition['geometry'] = {
  lanes: [],
  crosswalks: [],
  intersectionArea: {
    type: 'Feature',
    properties: { id: 'area' },
    geometry: { type: 'Polygon', coordinates: [[]] },
  },
};

// ~15-20m a la latitud de Santa Coloma — mismo orden de magnitud que el
// offset real entre cabinet y groups en reguladores-scg.ts.
const D_LNG = 0.00018;
const D_LAT = 0.00013;

/** Regulador sintético: 4 grupos vehiculares en cruz, misma topología de
 * intersección de 2 fases que usa gen-reguladores-scg.py para los 79 reales
 * — el admin no tiene datos de campo para grupos/fases reales todavía. */
function buildPlaceholderIntersection(a: NewRegulatorOverride): IntersectionDefinition {
  const [lng, lat] = a.position;
  return {
    id: `scg-reg-${a.logicalId}`,
    name: a.name || `Regulador ${a.logicalId}`,
    center: a.position,
    zoom: 18.5,
    bearing: 0,
    pitch: 50,
    cabinet: { position: a.position, logicalId: a.logicalId, physicalId: `SCG-R${a.logicalId}` },
    groups: [
      { id: 1, type: 'V', label: 'G1', position: [lng + D_LNG, lat], bearing: 270,
        phaseColors: { F1: CC.V, F2: CC.A, F3: CC.R, F4: CC.R } },
      { id: 2, type: 'V', label: 'G2', position: [lng - D_LNG, lat], bearing: 90,
        phaseColors: { F1: CC.V, F2: CC.A, F3: CC.R, F4: CC.R } },
      { id: 3, type: 'V', label: 'G3', position: [lng, lat + D_LAT], bearing: 180,
        phaseColors: { F1: CC.R, F2: CC.R, F3: CC.V, F4: CC.A } },
      { id: 4, type: 'V', label: 'G4', position: [lng, lat - D_LAT], bearing: 0,
        phaseColors: { F1: CC.R, F2: CC.R, F3: CC.V, F4: CC.A } },
    ],
    detectors: [],
    geometry: EMPTY_GEOMETRY,
  };
}

export function applyRegulatorLifecycle(
  base: IntersectionDefinition[],
  added: NewRegulatorOverride[],
  removedIds: number[],
): IntersectionDefinition[] {
  const removed = new Set(removedIds);
  const kept = base.filter(ix => !removed.has(ix.cabinet.logicalId));
  const newOnes = added
    .filter(a => !removed.has(a.logicalId))
    .map(buildPlaceholderIntersection);
  return [...kept, ...newOnes];
}
